import { useState, useEffect, cloneElement } from "react";
import { useLang } from "../contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import SpeakButton from "./SpeakButton";
import { findGuidedFlow, getLocalBankFlow } from "../lib/openaccount";
import {
  ArrowRight,
  ArrowLeft,
  Lightbulb,
  AlertTriangle,
  LifeBuoy,
  Check,
  Phone,
  Pause,
  Globe,
  Search,
  FileText,
  UserPlus,
  ClipboardEdit,
  Upload,
  PenLine,
  PartyPopper,
} from "lucide-react";

// One meaningful icon per typical bank-flow step (1-8).
// If a flow has more or fewer steps, we cycle through them safely.
const STEP_ICONS = [
  Globe,          // 1 — Go to the website
  Search,         // 2 — Find the product
  FileText,       // 3 — Prepare documents
  UserPlus,       // 4 — Create the account
  ClipboardEdit,  // 5 — Fill in the form
  Upload,         // 6 — Upload documents
  PenLine,        // 7 — Sign
  PartyPopper,    // 8 — Confirmation
];

const LOCAL_KEY = "sereno_guided_progress";

function loadProgress(key) {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return 0;
    const map = JSON.parse(raw);
    return map[key] || 0;
  } catch {
    return 0;
  }
}
function saveProgress(key, step) {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[key] = step;
    localStorage.setItem(LOCAL_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
}

export default function GuidedFlowDialog({
  optionId,
  providerName,
  trigger,
}) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const flow =
    optionId === "generic" && providerName === "local_bank"
      ? getLocalBankFlow()
      : findGuidedFlow(optionId, providerName);
  const localized = flow ? flow[lang] || flow.fr : null;
  const key = `${optionId}__${providerName}`;
  const [step, setStep] = useState(() => loadProgress(key));
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (open) setStep(loadProgress(key));
  }, [open, key]);

  if (!flow || !localized) return null;

  const total = localized.steps.length;
  const current = localized.steps[Math.min(step, total - 1)];
  const isLast = step >= total - 1;

  const goNext = () => {
    if (isLast) {
      saveProgress(key, total);
      setOpen(false);
      return;
    }
    const next = step + 1;
    setStep(next);
    saveProgress(key, next);
  };
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const pause = () => {
    saveProgress(key, step);
    setOpen(false);
  };

  const speakText = [
    current.title,
    current.body,
    current.highlight ? `Adresse : ${current.highlight}` : "",
    (current.tips || []).length
      ? (lang === "fr" ? "Astuces : " : "Tips: ") + (current.tips || []).join(". ")
      : "",
    current.warning
      ? (lang === "fr" ? "Attention : " : "Warning: ") + current.warning
      : "",
  ]
    .filter(Boolean)
    .join(". ");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ? (
          cloneElement(trigger)
        ) : (
          <Button data-testid="guided-flow-trigger">Ouvrir</Button>
        )}
      </DialogTrigger>
      <DialogContent
        className="max-w-2xl bg-white"
        data-testid="guided-flow-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#101A42] sm:text-3xl">
            {localized.title}
          </DialogTitle>
          <p className="text-base text-[#4A4A4A]">{localized.subtitle}</p>
        </DialogHeader>

        <div className="mt-2">
          <div className="mb-2 flex items-center justify-between text-sm text-[#4A4A4A]">
            <span data-testid="guided-progress-label">
              {t("guided_step")}{" "}
              <strong className="text-[#101A42]">{step + 1}</strong>{" "}
              {t("guided_of")} {total}
            </span>
            <span>{Math.round(((step + 1) / total) * 100)}%</span>
          </div>
          <Progress
            value={((step + 1) / total) * 100}
            className="h-2"
            data-testid="guided-progress"
          />
        </div>

        <div
          className="mt-4 rounded-2xl border-2 border-[#E8DFC7] bg-[#FFF7E9] p-5 sm:p-6"
          data-testid={`guided-step-${step + 1}`}
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <div className="flex flex-1 items-start gap-3">
              <div
                data-testid="guided-step-icon"
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#101A42] text-white shadow-[0_6px_18px_rgba(30,63,51,0.25)]"
              >
                {(() => {
                  const Icon = STEP_ICONS[step % STEP_ICONS.length];
                  return <Icon className="h-6 w-6" strokeWidth={2} />;
                })()}
              </div>
              <h4 className="font-serif text-xl font-semibold text-[#101A42] sm:text-2xl">
                {current.title}
              </h4>
            </div>
            <SpeakButton text={speakText} testid="guided-step-speak" />
          </div>

          <p className="whitespace-pre-line text-lg leading-relaxed text-[#101A42]">
            {current.body}
          </p>

          {current.highlight && (
            <div
              data-testid="guided-highlight"
              className="mt-4 rounded-xl border-2 border-[#101A42] bg-white p-4 font-mono text-lg font-semibold text-[#101A42] sm:text-xl"
            >
              {current.highlight}
            </div>
          )}

          {(current.tips || []).length > 0 && (
            <div className="mt-4 rounded-xl border border-[#A762B1]/30 bg-[#A762B1]/5 p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#A762B1]">
                <Lightbulb className="h-4 w-4" />
                {t("guided_tips")}
              </div>
              <ul className="space-y-1 text-base text-[#101A42]">
                {(current.tips || []).map((tip, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#A762B1]" />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {current.warning && (
            <div
              data-testid="guided-warning"
              className="mt-4 rounded-xl border-2 border-[#A762B1] bg-[#A762B1]/10 p-4"
            >
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#A762B1]">
                <AlertTriangle className="h-4 w-4" />
                {t("guided_warning")}
              </div>
              <p className="text-base leading-relaxed text-[#6E3978]">
                {current.warning}
              </p>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={goBack}
              disabled={step === 0}
              data-testid="guided-back-btn"
              className="min-h-[48px] rounded-full text-base text-[#101A42]"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("guided_back")}
            </Button>
            <Button
              variant="outline"
              onClick={pause}
              data-testid="guided-pause-btn"
              className="min-h-[48px] rounded-full border-2 border-[#101A42]/40 text-base text-[#101A42]"
            >
              <Pause className="mr-2 h-4 w-4" />
              {t("guided_pause")}
            </Button>
          </div>

          <Button
            onClick={goNext}
            data-testid="guided-next-btn"
            className="min-h-[56px] rounded-full bg-[#A762B1] px-6 text-lg font-semibold text-white hover:bg-[#B775C0]"
          >
            {isLast ? (
              <>
                <Check className="mr-2 h-5 w-5" />
                {t("guided_finish")}
              </>
            ) : (
              <>
                {t("guided_next")}
                <ArrowRight className="ml-2 h-5 w-5" />
              </>
            )}
          </Button>
        </div>

        <div className="mt-3 border-t border-[#E8DFC7] pt-3">
          <button
            type="button"
            onClick={() => setHelpOpen((v) => !v)}
            data-testid="guided-help-toggle"
            className="text-base font-semibold text-[#A762B1] underline underline-offset-4 hover:text-[#B775C0]"
          >
            <LifeBuoy className="mr-1 inline-block h-4 w-4" />
            {t("guided_help")}
          </button>
          {helpOpen && (
            <div
              data-testid="guided-help-panel"
              className="mt-3 grid gap-2 sm:grid-cols-3"
            >
              <a
                href="tel:+33146094949"
                data-testid="guided-help-call-btn"
                className="flex items-center justify-center gap-2 rounded-xl border-2 border-[#A762B1] bg-white px-4 py-3 text-base font-semibold text-[#A762B1] hover:bg-[#A762B1] hover:text-white"
              >
                <Phone className="h-4 w-4" />
                {t("guided_help_call")}
              </a>
              <button
                type="button"
                data-testid="guided-help-pause-btn"
                onClick={pause}
                className="flex items-center justify-center gap-2 rounded-xl border-2 border-[#101A42] bg-white px-4 py-3 text-base font-semibold text-[#101A42] hover:bg-[#101A42] hover:text-white"
              >
                <Pause className="h-4 w-4" />
                {t("guided_help_pause")}
              </button>
              <button
                type="button"
                data-testid="guided-help-share-btn"
                onClick={() => {
                  const text = encodeURIComponent(
                    `${localized.title}\n\n${current.title}\n${current.body}`
                  );
                  if (typeof window !== "undefined")
                    window.open(`https://wa.me/?text=${text}`, "_blank");
                }}
                className="flex items-center justify-center gap-2 rounded-xl border-2 border-[#25D366] bg-white px-4 py-3 text-base font-semibold text-[#25D366] hover:bg-[#25D366] hover:text-white"
              >
                {t("guided_help_share")}
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
